"use client";

import * as React from "react";
import { Checkbox as CheckboxPrimitive } from "@base-ui-components/react/checkbox";
import { cva, type VariantProps } from "class-variance-authority";

import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

const checkboxVariants = cva(
  cn(
    "group/checkbox peer relative inline-flex size-4 shrink-0 cursor-pointer items-center justify-center",
    "rounded-sm border-[1.25px] outline-none transition-colors",
    "border-[var(--input-border)] bg-[var(--input)]",
    "focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-1",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ),
  {
    variants: {
      variant: {
        default: cn(
          "text-white",
          "data-[checked]:border-primary data-[checked]:bg-primary",
          "data-[indeterminate]:border-primary data-[indeterminate]:bg-primary",
        ),
        brand: cn(
          "text-brand-foreground",
          "data-[checked]:border-brand data-[checked]:bg-brand",
          "data-[indeterminate]:border-brand data-[indeterminate]:bg-brand",
        ),
      },
    },
    defaultVariants: { variant: "default" },
  },
);

type CheckboxPrimitiveProps = React.ComponentProps<typeof CheckboxPrimitive.Root>;

export interface CheckboxProps
  extends Omit<CheckboxPrimitiveProps, "onCheckedChange" | "render" | "children">,
    VariantProps<typeof checkboxVariants> {
  /** Called with the new checked state. */
  onChange?: (checked: boolean) => void;
}

export function Checkbox({
  className,
  variant,
  onChange,
  ...props
}: CheckboxProps) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      onCheckedChange={(value) => onChange?.(value)}
      className={cn(checkboxVariants({ variant }), className)}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        keepMounted
        className="flex size-full items-center justify-center text-current"
      >
        <Icon
          name="IconCheckmark1Small"
          size={14}
          className="hidden text-current group-data-[checked]/checkbox:block"
        />
        <span
          aria-hidden="true"
          className="hidden h-[1.5px] w-[10px] rounded-[1px] bg-current group-data-[indeterminate]/checkbox:block"
        />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}
